import {
  FiCheck,
  FiClock,
  FiEye,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import LeaveStatusBadge from "./LeaveStatusBadge";

function formatDate(dateValue) {
  if (!dateValue) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${dateValue}T00:00:00`));
}

export default function LeaveRequestTable({
  requests,
  isLoading,
  updatingRequestId,
  onApprove,
  onReject,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-64 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Employee
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Leave Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Dates
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Days
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {requests.map((request) => {
              const initials = request.name
                .split(" ")
                .map((word) => word[0])
                .join("")
                .slice(0, 2);

              const isPending = request.status === "PENDING";
              const isUpdating = updatingRequestId === request.id;

              return (
                <tr
                  key={request.id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center font-semibold">
                        {initials || "EM"}
                      </div>

                      <div>
                        <p className="font-medium text-gray-900">
                          {request.name}
                        </p>

                        <p className="text-sm text-gray-500">
                          {request.employeeCode} · {request.designation}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-5 text-gray-700">
                    {request.leaveTypeName}
                  </td>

                  <td className="px-6 py-5">
                    <p className="text-gray-900">
                      {formatDate(request.startDate)}
                    </p>

                    <p className="text-sm text-gray-500">
                      to {formatDate(request.endDate)}
                    </p>
                  </td>

                  <td className="px-6 py-5 text-gray-700">
                    {request.totalDays} day
                    {request.totalDays === 1 ? "" : "s"}
                  </td>

                  <td className="px-6 py-5">
                    <LeaveStatusBadge status={request.displayStatus} />
                  </td>

                  <td className="px-6 py-5">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate(
                            `/admindashboard/leave/requests/${request.id}`,
                          )
                        }
                        className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        title="View leave request"
                      >
                        <FiEye />
                      </button>

                      {isPending ? (
                        <>
                          <button
                            disabled={isUpdating}
                            onClick={() => onApprove(request.id)}
                            className="p-2 rounded-xl bg-green-100 text-green-600 hover:bg-green-200 disabled:opacity-50"
                            title="Approve leave"
                          >
                            <FiCheck />
                          </button>

                          <button
                            disabled={isUpdating}
                            onClick={() => onReject(request.id)}
                            className="p-2 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50"
                            title="Reject leave"
                          >
                            <FiX />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}

            {!requests.length ? (
              <tr>
                <td
                  colSpan="6"
                  className="py-16 text-center text-gray-500"
                >
                  <FiClock className="mx-auto mb-3 text-gray-300" size={28} />
                  No leave requests found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}